import type { OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import type {
  CategoryPageViewUser,
  ProjectViewUser,
  SearchUser,
} from '@freelancer/datastore/collections';
import { FreelancerReputation } from '@freelancer/datastore/collections';
import { ButtonColor } from '@freelancer/ui/button';
import { CardLevel } from '@freelancer/ui/card';
import { IconColor } from '@freelancer/ui/icon';
import { LinkUnderline } from '@freelancer/ui/link';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import { UsernameSize } from '@freelancer/ui/username';

@Component({
  selector: 'fl-showcase-card-action',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShowcaseCardHoverActionComponent {}

@Component({
  selector: 'fl-showcase-card-hover-content',
  template: `
    <fl-card
      class="ShowcaseHoverContent-userInfo"
      [level]="CardLevel.SECONDARY"
      [edgeToEdge]="true"
      [maxContent]="false"
    >
      <div class="ShowcaseHoverContent">
        <ng-container *ngIf="user; else loadingState">
          <fl-link
            class="ShowcaseHoverContent-link"
            [underline]="LinkUnderline.NEVER"
            [link]="hireLink"
          >
            <div class="ShowcaseHoverContent-userData">
              <fl-user-avatar
                [size]="AvatarSize.LARGE"
                [users]="[user]"
                [flMarginRight]="'xsmall'"
              ></fl-user-avatar>
              <fl-user-info
                class="ShowcaseHoverContent-userInfoContent"
                [user]="user"
                [ratingControl]="ratingControl"
                [freelancerReputation]="reputation"
                [usernameSize]="UsernameSize.LARGE"
                [usernameSizeDesktop]="UsernameSize.XLARGE"
                [showCity]="showCity"
                [flMarginRight]="'xsmall'"
              >
              </fl-user-info>
            </div>
          </fl-link>
        </ng-container>
        <ng-template #loadingState>
          <div class="ShowcaseHoverContent-userData">
            <div class="ShowcaseHoverContent-loading">
              <fl-loading-text
                class="ShowcaseHoverContent-loading-profilePicture"
                [padded]="false"
                [flMarginRight]="'xsmall'"
              ></fl-loading-text>

              <div class="ShowcaseHoverContent-loading-text">
                <fl-loading-text
                  class="ShowcaseHoverContent-loading-text--firstLine"
                  [padded]="false"
                ></fl-loading-text>
                <fl-loading-text
                  class="ShowcaseHoverContent-loading-text--secondLine"
                  [padded]="false"
                ></fl-loading-text>
              </div>
            </div>
          </div>
        </ng-template>

        <div
          *ngIf="user"
          class="ShowcaseHoverContent-actionItem"
        >
          <ng-content select="fl-showcase-card-action"></ng-content>
          <fl-button
            i18n="Hire Freelancer Button"
            [color]="ButtonColor.PRIMARY"
            [link]="hireLink"
          >
            Hire
          </fl-button>
        </div>
      </div>
    </fl-card>
    <fl-showcase-card-price
      *ngIf="cost !== undefined"
      class="ShowcaseHoverContent-cost"
      [cost]="cost"
      [label]="label"
    ></fl-showcase-card-price>
  `,
  styleUrls: ['./showcase-card-hover-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShowcaseCardHoverContentComponent implements OnChanges {
  AvatarSize = AvatarSize;
  ButtonColor = ButtonColor;
  CardLevel = CardLevel;
  LinkUnderline = LinkUnderline;
  UsernameSize = UsernameSize;
  IconColor = IconColor;
  hireLink: string;

  @Input() user?: ProjectViewUser | SearchUser | CategoryPageViewUser;
  @Input() ratingControl = new FormControl(0, { nonNullable: true });
  @Input() reputation?: FreelancerReputation;
  @Input() cost?: number;
  @Input() label?: string;
  @Input() showCity? = true;

  ngOnChanges(changes: SimpleChanges): void {
    if ('user' in changes && this.user) {
      this.hireLink = `/u/${this.user.username}`;
    }
  }
}
